import { type ReactNode, useEffect, useState } from "react";
import { PRICE } from "./content.ts";

/** Counts up to a value once, unless the visitor prefers less motion. */
function useCountUp(target: number, ms = 900) {
  const [v, setV] = useState(target);
  useEffect(() => {
    // Only when someone can see it, so a page opened in the background still shows the real number.
    if (
      document.visibilityState !== "visible" ||
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
    )
      return;
    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const p = Math.min(1, (now - start) / ms);
      setV(target * (1 - (1 - p) ** 3));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    setV(0);
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, ms]);
  return v;
}

const usd = (n: number, cents = false) =>
  n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: cents ? 2 : 0,
    maximumFractionDigits: cents ? 2 : 0,
  });

/**
 * A real outage, measured the way ninekeep measures it: Snowflake's AWS Europe (Ireland) region,
 * 21 October 2026 (INC20000301 on status.snowflake.com, major outage 18:26–23:29 UTC), under
 * Snowflake's published SLA: below 99.9% for the month pays one day's average usage, claimed
 * within 21 days of the month's end. The fee is an example; the credit scales with it.
 */
export const REAL_EXAMPLE = {
  vendor: "Snowflake",
  incident: "INC20000301",
  fee: 31_000,
  measured: "99.32%",
  /** One day's average usage: the month's fee over October's 31 days. */
  credit: 1_000,
  claimBy: "Nov 21",
} as const;

/** The money, shown the way it arrives: a credit memo against the vendor's next invoice. */
export function CreditMemo() {
  const x = REAL_EXAMPLE;
  const credit = useCountUp(x.credit);
  const rows: [string, string][] = [
    [`Fee, ${x.vendor}`, `${usd(x.fee)} / mo`],
    ["Promised uptime", "99.9%"],
    ["Measured in October", x.measured],
    ["Below 99.9% pays back", "1 day's usage"],
  ];
  return (
    <div className="rise relative mx-auto w-full max-w-md">
      <div className="memo rounded-b-card px-6 pt-8 pb-6 shadow-hero">
        <div className="flex items-baseline justify-between gap-3 eyebrow">
          <span>Credit memo · Oct 2026</span>
        </div>
        <dl className="mt-5 flex flex-col gap-2.5 font-mono text-sm">
          {rows.map(([k, v]) => (
            <div
              key={k}
              className="flex justify-between gap-4 border-b border-dashed border-line pb-2.5"
            >
              <dt className="text-muted">{k}</dt>
              <dd className="tabular text-ink">{v}</dd>
            </div>
          ))}
        </dl>
        <div className="mt-5 flex items-end justify-between gap-4">
          <span className="text-sm font-medium text-muted">Owed to you</span>
          <span className="tabular font-display text-5xl font-bold tracking-tight text-ink">
            <span className="marker">{usd(credit, true)}</span>
          </span>
        </div>
        <p className="mt-4 font-mono text-label text-faint">
          {x.incident} · claim by {x.claimBy} · proof attached
        </p>
      </div>
      <span className="stamp stamp-in absolute top-6 right-5 bg-memo/80 px-2 py-1 font-mono text-xs font-semibold text-accent">
        OWED
      </span>
    </div>
  );
}

/** A real SLA's three parts, marked the way a person reads one with a highlighter. */
export function ClauseCard() {
  return (
    <figure className="rounded-card border border-line bg-surface p-6 shadow-card">
      <figcaption className="eyebrow">How a typical contract reads</figcaption>
      <blockquote className="mt-4 font-serif text-prose leading-relaxed text-ink">
        The service will be available with a monthly uptime percentage of{" "}
        <mark className="mark-clause">at least 99.9%</mark>. If it falls short, customer is eligible
        for <mark className="mark-clause">a service credit of 10% of the monthly fee</mark>. To
        receive a credit, customer must submit a request{" "}
        <mark className="mark-clause">within 15 days after the end of the month</mark>.
      </blockquote>
      <ol className="mt-5 grid gap-3 text-sm sm:grid-cols-3">
        {[
          ["The promise", "What the vendor promised"],
          ["The money back", "What a miss is worth"],
          ["The deadline", "After this, it's gone"],
        ].map(([t, b], i) => (
          <li key={t} className="flex gap-2.5">
            <span className="tabular flex size-6 shrink-0 items-center justify-center rounded-full bg-hl font-mono text-xs font-semibold text-hl-ink">
              {i + 1}
            </span>
            <span>
              <span className="block font-semibold">{t}</span>
              <span className="text-muted">{b}</span>
            </span>
          </li>
        ))}
      </ol>
    </figure>
  );
}

/** The whole price, three lines: what's free, what's monthly, and what's shared. */
export function PriceBreakdown() {
  const rows: [string, string, string][] = [
    ["Free audit", "$0", `Up to ${PRICE.auditContracts} contracts and the last 12 months`],
    [
      "Monitoring",
      `$${PRICE.perVendor} per vendor a month`,
      `At least ${PRICE.minVendors} vendors, so from $${PRICE.perVendor * PRICE.minVendors} a month`,
    ],
    ["Recovery", `${PRICE.successFee}% of what comes back`, "Only once the credit is on your bill"],
  ];
  return (
    <dl className="flex flex-col divide-y divide-white/15 rounded-panel bg-white/5 px-6 text-band-ink">
      {rows.map(([k, v, note]) => (
        <div key={k} className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 py-5">
          <dt className="text-sm text-band-muted">{k}</dt>
          <dd className="text-right">
            <span className="font-display text-2xl font-bold tracking-tight">{v}</span>
            <span className="block text-sm text-band-muted">{note}</span>
          </dd>
        </div>
      ))}
    </dl>
  );
}

// ------------------------------------------------------------------ cards for the deeper pages
// Each is an example, marked as one, drawn the way the app shows the same thing.

const Card = ({ label, children }: { label: string; children: ReactNode }) => (
  <figure className="rise rounded-card border border-line bg-surface p-6 shadow-card">
    <figcaption className="flex items-center justify-between gap-3 eyebrow">
      <span>{label}</span>
      <span className="text-faint">Example</span>
    </figcaption>
    <div className="mt-5">{children}</div>
  </figure>
);

/** Claim windows, each closing on its own clock. */
export function DeadlineCard() {
  const rows: [string, string, number, string][] = [
    ["Atlassian Cloud", "15 days after the month ends", 0.6, "6 days left"],
    ["AWS EC2", "By the end of the second billing cycle", 0.25, "41 days left"],
    ["Ticketing vendor", "Report during the outage", 0.95, "Report now"],
  ];
  return (
    <Card label="Claim deadlines">
      <ul className="flex flex-col gap-4">
        {rows.map(([v, rule, used, left]) => (
          <li key={v}>
            <div className="flex items-baseline justify-between gap-3">
              <span className="font-semibold">{v}</span>
              <span
                className={
                  used > 0.9 ? "font-mono text-xs text-danger" : "font-mono text-xs text-muted"
                }
              >
                {left}
              </span>
            </div>
            <p className="text-sm text-muted">{rule}</p>
            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-surface-2">
              <div
                className={used > 0.9 ? "h-full bg-danger" : "h-full bg-hl"}
                style={{ width: `${used * 100}%` }}
              />
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/** A vendor's year, month by month: the record you bring to a renewal. */
export function ScorecardCard() {
  const missed = new Set([2, 6, 7]);
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return (
    <Card label="Vendor scorecard · last 12 months">
      <p className="font-display text-xl font-bold tracking-tight">Data warehouse vendor</p>
      <p className="text-sm text-muted">Promised 99.9% every month</p>
      <ol className="mt-4 grid grid-cols-12 gap-1.5">
        {months.map((m, i) => (
          <li key={m} className="flex flex-col items-center gap-1">
            <span
              className={
                missed.has(i)
                  ? "h-8 w-full rounded-chip bg-danger"
                  : "h-8 w-full rounded-chip bg-ok"
              }
              title={missed.has(i) ? "Missed" : "Kept"}
            />
            <span className="font-mono text-label text-faint">{m[0]}</span>
          </li>
        ))}
      </ol>
      <dl className="mt-5 grid grid-cols-3 gap-3 border-t border-dashed border-line pt-4 font-mono text-sm">
        {[
          ["Missed", "3 of 12"],
          ["Claimed", "$2,400"],
          ["Renews", "In 60 days"],
        ].map(([k, v]) => (
          <div key={k}>
            <dt className="text-label text-faint uppercase">{k}</dt>
            <dd className="tabular text-ink">{v}</dd>
          </div>
        ))}
      </dl>
    </Card>
  );
}

/** Every company in a portfolio, each with its own audit. */
export function PortfolioCard() {
  const rows: [string, string, string][] = [
    ["Company A", "Audit done", "$18,400 owed"],
    ["Company B", "Audit done", "$6,250 owed"],
    ["Company C", "Contracts in", "Reading"],
    ["Company D", "Invited", "Not started"],
  ];
  return (
    <Card label="Across your companies">
      <ul className="divide-y divide-line">
        {rows.map(([c, s, v]) => (
          <li key={c} className="flex items-center justify-between gap-3 py-3 first:pt-0 last:pb-0">
            <span>
              <span className="block font-semibold">{c}</span>
              <span className="text-sm text-muted">{s}</span>
            </span>
            <span className="tabular font-mono text-sm text-ink">{v}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/** Your vendors in three groups: who pays you back, who doesn't, and who has no contract yet. */
export function SortedCard() {
  const groups: [string, number, string][] = [
    ["Pays you back", 7, "bg-ok"],
    ["Pays nothing when it misses", 4, "bg-warn"],
    ["No contract uploaded yet", 3, "bg-line-strong"],
  ];
  const total = groups.reduce((n, g) => n + g[1], 0);
  return (
    <Card label="Your vendors, sorted">
      <div className="flex h-3 overflow-hidden rounded-full">
        {groups.map(([k, n, c]) => (
          <span key={k} className={c} style={{ width: `${(n / total) * 100}%` }} />
        ))}
      </div>
      <ul className="mt-5 flex flex-col gap-3">
        {groups.map(([k, n, c]) => (
          <li key={k} className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-2.5">
              <span className={`size-2.5 rounded-full ${c}`} aria-hidden />
              {k}
            </span>
            <span className="tabular font-mono text-ink">{n}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/** One vendor watched three ways, and the outage they caught. */
export function ChecksCard() {
  const rows: [string, string, boolean][] = [
    ["Vendor status page", "Major outage, 5 h", false],
    ["ninekeep's own checks", "Down 18:26–23:29", false],
    ["Outage email you forwarded", "Received 18:40", true],
  ];
  return (
    <Card label="Watched three ways">
      <div className="flex h-8 items-end gap-0.5" aria-hidden>
        {Array.from({ length: 30 }, (_, i) => `day-${i + 1}`).map((day, i) => (
          <span
            key={day}
            className={
              i === 20 ? "h-full flex-1 rounded-sm bg-danger" : "h-1/2 flex-1 rounded-sm bg-ok"
            }
          />
        ))}
      </div>
      <p className="mt-1 font-mono text-label text-faint">October · one bad day</p>
      <ul className="mt-5 flex flex-col gap-2.5 text-sm">
        {rows.map(([k, v]) => (
          <li key={k} className="flex items-center justify-between gap-3">
            <span className="text-muted">{k}</span>
            <span className="font-mono text-ink">{v}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/** Money recovered so far, and where each claim stands. */
export function RecoveredCard() {
  const total = useCountUp(12_850);
  const rows: [string, string, string][] = [
    ["Cloud hosting · Sep", "$4,200", "Credited"],
    ["Data warehouse · Oct", "$1,000", "Sent"],
    ["Ticketing · Oct", "$650", "Ready to send"],
  ];
  return (
    <Card label="Recovered this year">
      <p className="tabular font-display text-5xl font-bold tracking-tight">
        <span className="marker">{usd(total)}</span>
      </p>
      <ul className="mt-5 divide-y divide-line text-sm">
        {rows.map(([k, v, s]) => (
          <li key={k} className="flex items-center justify-between gap-3 py-2.5">
            <span className="text-muted">{k}</span>
            <span className="flex items-center gap-3">
              <span className="tabular font-mono text-ink">{v}</span>
              <span
                className={
                  s === "Credited"
                    ? "rounded-full bg-ok-soft px-2 py-0.5 text-xs font-medium text-ok"
                    : "rounded-full bg-surface-2 px-2 py-0.5 text-xs font-medium text-muted"
                }
              >
                {s}
              </span>
            </span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/** The smallest plan, priced in full. */
export function SmallPlanCard() {
  return (
    <Card label="The smallest plan">
      <p className="font-display text-5xl font-bold tracking-tight">
        ${PRICE.perVendor * PRICE.minVendors}
        <span className="text-base font-medium text-muted"> a month</span>
      </p>
      <ul className="mt-5 flex flex-col gap-2.5 text-sm">
        {[
          `Up to ${PRICE.minVendors} vendors monitored`,
          `${PRICE.successFee}% of what comes back, only once it's on your bill`,
          `A free audit first: up to ${PRICE.auditContracts} contracts, the last 12 months`,
        ].map((t) => (
          <li key={t} className="flex gap-2.5">
            <span className="mt-2 h-1 w-3 shrink-0 rounded-full bg-hl" aria-hidden />
            {t}
          </li>
        ))}
      </ul>
    </Card>
  );
}
